<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { MapSource, MapLayer } from '@/components/map'
import { API, HOST } from '@/utils/http'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'

const store = useStore()
const route = useRoute()
const map = computed(() => props.map)
const layerManager = useLayerManager(map)

const props = defineProps({
  map: { type: Object, required: true },
  beforeId: { type: String, required: true },
  groupId: { type: String, required: true },
  layerId: { type: String, required: true },
  orgId: { type: Number, required: true },
  isVisible: { type: Boolean, default: true },
  aoi: { type: Object, default: null },
  layerUuid: { type: String, default: null },
})

const emit = defineEmits(['isLoaded'])

const borderSourceId = computed(() => createSourceId(props.groupId, 'lulc-border-source'))
const borderLayerId = computed(() => createLayerId(props.groupId, 'lulc-border'))

const aoiGeoJson = computed(() => {
  if (!props.aoi) return null
  return { type: 'Feature', geometry: props.aoi, properties: {} }
})

// Status polling — while a LULC layer is still training / classifying / tiling,
// refresh its row every 30 seconds so the parent can swap in the classified
// variant as soon as status flips to 0 (done).
const pollingInterval = ref(null)
const layerUuid = computed(() => props.layerUuid || props.layerId.replace(/^layer-/, ''))
const routeOrgId = computed(() => route.params?.org_id || props.orgId)

// LULC progress lives on per-month rows, not a single status on the parent.
// Terminal == one of: any month done (parent swaps to LayerLulcClassified
// the moment that flips), or every remaining month in a failed state.
const isTerminalLulcResponse = (data) => {
  if (!data) return true  // nothing useful coming back; no point polling
  const months = data.layer?.months
  if (Array.isArray(months) && months.length > 0) {
    const anyDone = months.some(m => Number(m.status) === 0)
    const allTerminal = months.every(m => {
      const s = Number(m.status)
      return s === 0 || (Number.isFinite(s) && s < 0)
    })
    return anyDone || allTerminal
  }
  // Non-LULC fallback shape (kept for safety even though this component is
  // LULC-only today): a single nested status that's terminal.
  const s = data.layer?.status
  return s === 0 || (typeof s === 'number' && s < 0)
}

const fetchLayerStatus = async () => {
  if (!layerUuid.value || !routeOrgId.value) return
  if (!pollingInterval.value) return  // raced with stopPolling
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${routeOrgId.value}/layers/${layerUuid.value}`,
    })
    if (response.data) {
      store.dispatch('layers/updateOrgLayer', {
        orgId: routeOrgId.value,
        layer: response.data,
      })
    }
    if (isTerminalLulcResponse(response.data)) {
      stopPolling()
    }
  } catch (err) {
    console.debug('LULC status poll failed:', err?.message || err)
  }
}

const startPolling = () => {
  if (!pollingInterval.value) {
    // Stagger the initial fetch so N preview components mounting together
    // don't all hammer apiv3 at the same instant.
    const jitter = Math.floor(Math.random() * 5000)
    pollingInterval.value = setInterval(fetchLayerStatus, 30000)
    setTimeout(fetchLayerStatus, jitter)
  }
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

onMounted(() => {
  layerManager.registerLayer(props.groupId, borderLayerId.value)
  emit('isLoaded', true)
  startPolling()
})

onUnmounted(() => {
  stopPolling()
  layerManager.unregisterLayer(props.groupId, borderLayerId.value)
})
</script>

<template>
  <div v-if="map">
    <MapSource
      v-if="aoiGeoJson"
      :map="map"
      type="geojson"
      :id="borderSourceId"
      :data="aoiGeoJson"
    >
      <MapLayer
        :map="map"
        :id="borderLayerId"
        type="line"
        :interactive="false"
        :source="borderSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="{
          paint: {
            'line-color': '#3b82f6',
            'line-width': 2,
            'line-opacity': 0.9,
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none',
          },
        }"
      />
    </MapSource>
  </div>
</template>
